// AppRoutes.jsx
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Cookies from "js-cookie";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Box } from "@chakra-ui/react";
import { USER_LEVELS } from "../util/data";
import { useState, useEffect, useMemo } from "react";

// Import components
import Welcome from "../application/Welcome";

import Login from "../auth/Login";
import ForgotPassword from "../auth/ForgotPassword";
import Verify from "../auth/Verify";
import ResetPassword from "../auth/ResetPassword";
import Error404 from "../pages/Error404";
import PersonalInfo from "../application/PersonalInfo";
import WorkHistory from "../application/WorkHistory";
import Certificate from "../application/Certificates";
import UploadResume from "../application/UploadResume";
import Address from "../application/Address";
import UploadPOI from "../application/UploadPOI";
import Nok from "../application/Nok";
import MobilityStatus from "../application/MobilityStatus";
import RTWUpload from "../application/RTWUpload";
import InterviewScheduler from "../Interview/InterviewScheduler";
import InterviewFeedbackForm from "../Interview/InterviewFeedbackForm";
import Reference from "../onboarding/Reference";
import Dashboard from "../dashboard/Dashboard";
import Compliance from "../dashboard/compliance/Compliance";
import Timesheet from "../dashboard/TimeSheet";
import Profile from "../dashboard/profile/Profile";
import { Referee } from "../onboarding/Referee";
import DBS from "../offer/DBS";
import useUserContext, { AuthProvider } from "../components/UserContext";
import ProofOfWork from "../onboarding/ProofOfWork";
import Provisional from "../onboarding/Provisional";
import ContractAgreement from "../agreement/ContractAgreement";
import FeedbackForm from "../pages/FeedBack";
import AdminContractAgreement from "../agreement/AdminCopy";
import Uniform from "../finalstep/Uniform";
import BankDetails from "../finalstep/BankDetails";
import NightWorker from "../finalstep/NightWorker";
import OptoutAgreement from "../finalstep/OptoutAgreement";
import CheckList from "../finalstep/StarterCheckList";
import AdminOptOutAgreement from "../finalstep/AdminOptOut";
import UploadPassportPhoto from "../application/UploadPassportPhoto";

import ApplicationStageHandler from "../components/ApplicationStageHandler";
import ApplicationSubmitted from "../components/ApplicationSubmitted";
import Register from "../auth/Resgister";
import ProfileSummary from "../summary/ProfileSummarry";


// Custom hook for authentication state
const useAuthState = () => {
    const [authState, setAuthState] = useState({
        userLevel: null,
        isLoggedIn: false,
        isLoading: true
    });

    useEffect(() => {
        const checkAuthState = () => {
            try {
                const userLevel = 
                    sessionStorage.getItem("userLevel") ||
                    localStorage.getItem("currentUserLevel") ||
                    Cookies.get("userLevel");

                const isLoggedIn = 
                    sessionStorage.getItem("isLoggedin") === "true" ||
                    Cookies.get("isLoggedin") === "true";

                setAuthState({
                    userLevel: userLevel ? parseInt(userLevel, 10) : null,
                    isLoggedIn: !!isLoggedIn,
                    isLoading: false
                });
            } catch (error) {
                console.error('Error checking auth state:', error);
                setAuthState(prev => ({ ...prev, isLoading: false }));
            }
        };

        checkAuthState();

        const handleStorageChange = () => checkAuthState();
        const interval = setInterval(checkAuthState, 1000);

        window.addEventListener('storage', handleStorageChange);
        return () => {
            clearInterval(interval);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    return authState;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredLevel, fallbackPath = "/" }) => {
    const { userLevel, isLoggedIn, isLoading } = useAuthState();

    if (isLoading) {
        return (
            <Box py="17em" alignItems="center" justifyItems="center" height="100vh">
                <LoadingSpinner />
            </Box>
        );
    }

    if (!isLoggedIn) {
        return <Navigate to="/login" replace />;
    }

    if (userLevel === null || userLevel < requiredLevel) {
        return <Navigate to={fallbackPath} replace />;
    }

    return <AuthProvider>{children}</AuthProvider>;
};

// Public Route Component
const PublicRoute = ({ children }) => {
    const { isLoggedIn, isLoading } = useAuthState();

    if (isLoading) {
        return (
            <Box py="17em" alignItems="center" justifyItems="center" height="100vh">
                <LoadingSpinner />
            </Box>
        );
    }

    if (isLoggedIn) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

export const AppRoutes = () => {
    const { loading } = useUserContext();
    const { isLoading } = useAuthState();

    if (loading || isLoading) {
        return (
            <Box py="17em" alignItems="center" justifyItems="center" height="100vh">
                <LoadingSpinner />
            </Box>
        );
    }

    return (
        <Router>
            <AuthProvider>
                <ApplicationStageHandler>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<Welcome />} />
                        <Route path="/feedback" element={<FeedbackForm />} />
                        
                        {/* Authentication Routes */}
                        <Route path="/register" element={
                            <PublicRoute>
                                <Register />
                            </PublicRoute>
                        } />
                        <Route path="/login" element={
                            <PublicRoute>
                                <Login />
                            </PublicRoute>
                        } />
                        <Route path="/forgot-password" element={
                            <PublicRoute>
                                <ForgotPassword />
                            </PublicRoute>
                        } />
                        <Route path="/verify" element={
                            <PublicRoute>
                                <Verify />
                            </PublicRoute>
                        } />
                        
                        {/* Utility Routes */}
                        <Route path="/reset-password/:user_id/:reset_token" element={<ResetPassword />} />
                        <Route path="/download-agreement/:user_id" element={<AdminContractAgreement />} />
                        <Route path="/profile-summary/:user_id" element={<ProfileSummary />} />
                        <Route path="/download-agreement/opt-out/:user_id" element={<AdminOptOutAgreement />} />
                        <Route path="/reference/:token" element={<Reference />} />

                        {/* Application Submitted */}
                        <Route path="/application-submitted" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <ApplicationSubmitted />
                            </ProtectedRoute>
                        } />

                        {/* Application Routes */}
                        <Route path="/application/personal-info" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <PersonalInfo />
                            </ProtectedRoute>
                        } />
                        <Route path="/application/work-history" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <WorkHistory />
                            </ProtectedRoute>
                        } />
                        <Route path="/application/training/certificate" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <Certificate />
                            </ProtectedRoute>
                        } />
                        <Route path="/application/resume" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <UploadResume />
                            </ProtectedRoute>
                        } />
                        <Route path="/application/address" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <Address />
                            </ProtectedRoute>
                        } />
                        <Route path="/application/upload-passport" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <UploadPassportPhoto />
                            </ProtectedRoute>
                        } />
                        <Route path="/application/id-upload" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <UploadPOI />
                            </ProtectedRoute>
                        } />
                        <Route path="/application/next-of-kin" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <Nok />
                            </ProtectedRoute>
                        } />
                        <Route path="/application/mobility-status" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <MobilityStatus />
                            </ProtectedRoute>
                        } />
                        <Route path="/application/right-to-work" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.APPLICATION_PROCESS}>
                                <RTWUpload />
                            </ProtectedRoute>
                        } />

                        {/* Interview Routes */}
                        <Route path="/interview/schedule" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.INTERVIEW}>
                                <InterviewScheduler />
                            </ProtectedRoute>
                        } />
                        <Route path="/interview/feedback/:applicant_token" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.INTERVIEW}>
                                <InterviewFeedbackForm />
                            </ProtectedRoute>
                        } />

                        {/* Pre-employment Routes */}
                        <Route path="/employment-check/provisional-offer" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.PRE_EMPLOYMENT_CHECK}>
                                <Provisional />
                            </ProtectedRoute>
                        } />
                        <Route path="/employment-check/rtw-proof" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.PRE_EMPLOYMENT_CHECK}>
                                <ProofOfWork />
                            </ProtectedRoute>
                        } />
                        <Route path="/employment-check/dbs" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.PRE_EMPLOYMENT_CHECK}>
                                <DBS />
                            </ProtectedRoute>
                        } />
                        <Route path="/employment-check/referee" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.PRE_EMPLOYMENT_CHECK}>
                                <Referee />
                            </ProtectedRoute>
                        } />

                        {/* Onboarding Routes */}
                        <Route path="/onboarding/checklist" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.CONTRACT_AGREEMENT}>
                                <CheckList />
                            </ProtectedRoute>
                        } />
                        <Route path="/onboarding/uniform" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.CONTRACT_AGREEMENT}>
                                <Uniform />
                            </ProtectedRoute>
                        } />
                        <Route path="/onboarding/bank" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.CONTRACT_AGREEMENT}>
                                <BankDetails />
                            </ProtectedRoute>
                        } />
                        <Route path="/onboarding/nightwork" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.CONTRACT_AGREEMENT}>
                                <NightWorker />
                            </ProtectedRoute>
                        } />
                        <Route path="/onboarding/optout-agreement" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.CONTRACT_AGREEMENT}>
                                <OptoutAgreement />
                            </ProtectedRoute>
                        } />
                        <Route path="/contract-agreement" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.CONTRACT_AGREEMENT}>
                                <ContractAgreement />
                            </ProtectedRoute>
                        } />
                         <Route path="/summary/" element={ <ProtectedRoute requiredLevel={USER_LEVELS.CONTRACT_AGREEMENT}>
                                <ProfileSummary />
                            </ProtectedRoute>} />

                        {/* Staff Routes */}
                        <Route path="/dashboard" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.STAFF}>
                               " <Dashboard />"
                            </ProtectedRoute>
                        } />
                        <Route path="/dashboard/compliance" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.STAFF}>
                                <Compliance />
                            </ProtectedRoute>
                        } />
                        <Route path="/dashboard/timesheet" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.STAFF}>
                                <Timesheet />
                            </ProtectedRoute>
                        } />
                        <Route path="/dashboard/profile/edit" element={
                            <ProtectedRoute requiredLevel={USER_LEVELS.STAFF}>
                                <Profile />
                            </ProtectedRoute>
                        } />

                        {/* Catch-all Route */}
                        <Route path="*" element={<Error404 />} />
                    </Routes>
                </ApplicationStageHandler>
            </AuthProvider>
        </Router>
    );
};